import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import FileCopy from "@mui/icons-material/FileCopy";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import AutocompleteWithQuery from "components/AutocompleteWithQuery";
import ConfirmDialog from "components/ConfirmDialog";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  AdminListRequestOfBaseFilter,
  AdminListRequestOfEvaluationFilter,
  Evaluation,
  EvaluationFilter,
  IEvaluation,
  ListResponseOfEvaluation,
  ListResponseOfEvaluationType,
  ResponseOfEvaluation,
} from "services/KnowfullyAdminClient";
import { Organizations } from "@knowfully/ui.common";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import translateError from "utils/translateError";

interface IEvaluationWithId extends IEvaluation {
  id: string;
}

const EvaluationList = () => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    evaluationService,
    evaluationDetailsUrl,
    evaluationCreateUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const [cloning, setCloning] = useState<Evaluation | null>(null);
  const [confirmOpenClone, confirmOpenActions] = useBoolean(false);
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("createdOn");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState(
    (s) => s.evaluationFilterSearchTerm
  );
  const debouncedSearchTerm = useDebounce(searchTerm?.trim(), 500);
  const evaluationTypeId = persistanceStore.useState(
    (s) => s.evaluationFilterTypeId
  );
  const isActive = persistanceStore.useState((s) => s.evaluationFilterIsActive);
  const isRootOrganization =
    organizationId === Organizations.KnowfullyLearningGroup;
  const createDisabled = !organizationId || isRootOrganization;

  useListTitle("evaluationsLabel");

  const {
    isError,
    data,
    error,
    isFetching,
  } = useQuery<ListResponseOfEvaluation>(
    [
      "evaluations",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
      evaluationTypeId,
      isActive,
    ],
    () =>
      evaluationService.getEvaluations(
        AdminListRequestOfEvaluationFilter.fromJS({
          organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: EvaluationFilter.fromJS({
            evaluationTypeId,
            isActive,
            searchText: debouncedSearchTerm,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const fetchedEvaluationTypes = useQuery<ListResponseOfEvaluationType>(
    "evaluationTypes",
    () =>
      evaluationService.getEvaluationTypes(
        new AdminListRequestOfBaseFilter({ sortField: "name" })
      )
  );

  const evaluations = data?.data as IEvaluationWithId[];

  const CloneButton = ({ value }: { value: Evaluation }) => {
    const handleClick = useCallback(
      (e: any) => {
        e.stopPropagation();
        setCloning(value);
        confirmOpenActions.setTrue();
      },
      [value]
    );
    return (
      <Box textAlign="center">
        <IconButton size="small" onClick={handleClick}>
          <FileCopy />
        </IconButton>
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "evaluationType.name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "evaluationsTypeLabel" }),
    },
    {
      accessor: "description",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "descriptionLabel" }),
    },
    {
      accessor: "isActive",
      align: "left",
      width: 0,
      type: "boolean",
      label: t({ id: "isActiveLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: CloneButton,
      noSort: true,
      label: t({ id: "evaluationCloneLabel" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(evaluationDetailsUrl(id));
  };

  const onCloneConfirm = useCallback(async () => {
    setCloning(null);
    const evaluationId = cloning?.id;

    if (evaluationId) {
      try {
        var response: ResponseOfEvaluation = await registerPromise(
          evaluationService.cloneEvaluation(evaluationId)
        );
        confirmOpenActions.setFalse();
        navigate(evaluationDetailsUrl(response?.data?.id));
      } catch (error: any) {
        return { [FORM_ERROR]: translateError(error, t) };
      }
    }
  }, [
    cloning?.id,
    confirmOpenActions,
    evaluationDetailsUrl,
    evaluationService,
    navigate,
    registerPromise,
    t,
  ]);

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <Container>
      <Box mb={3} justifyContent="center" display="flex" gap={2}>
        {createDisabled ? (
          <Tooltip
            title={
              isRootOrganization
                ? t({ id: "rootOrganizationIsNotAllowedTooltip" })
                : ""
            }
          >
            <span>
              <Fab aria-label="add" color="primary" disabled>
                <AddIcon />
              </Fab>
            </span>
          </Tooltip>
        ) : (
          <Link to={evaluationCreateUrl()} style={{ textDecoration: "none" }}>
            <Fab aria-label="add" color="primary">
              <AddIcon />
            </Fab>
          </Link>
        )}
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.evaluationFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.evaluationFilterSearchTerm = e.target.value;
                })
              }
            />
          </Grid>
          <Grid item sm={12} md={4}>
            <AutocompleteWithQuery
              value={evaluationTypeId}
              onChange={(id) =>
                persistanceStore.update((s) => {
                  s.evaluationFilterTypeId = id;
                })
              }
              labelField="name"
              fetchedData={fetchedEvaluationTypes}
              failMessage={t({
                id: "selectEvaluationTypeFetchFailed",
              })}
              textFieldProps={{
                variant: "outlined",
              }}
              label={t({ id: "evaluationTypeLabel" })}
            />
          </Grid>
          <Grid item sm={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="isActiveSelect">
                {t({ id: "isActiveLabel" })}
              </InputLabel>
              <Select
                labelId="isActiveSelect"
                fullWidth
                value={isActive ?? ""}
                label={t({ id: "isActiveLabel" })}
                onChange={(e) =>
                  persistanceStore.update((s) => {
                    s.evaluationFilterIsActive = e.target.value as any;
                  })
                }
                variant="outlined"
              >
                <MenuItem>{t({ id: "anyLabel" })}</MenuItem>
                <MenuItem value={true as any}>
                  {t({ id: "activeLabel" })}
                </MenuItem>
                <MenuItem value={false as any}>
                  {t({ id: "inactiveLabel" })}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: evaluations ?? [],
          loading: isFetching,

          dense: true,
          title: t({ id: "evaluationsLabel" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => evaluationDetailsUrl(id)),
          ],
        }}
      />

      <ConfirmDialog
        dialogTitleKey="evaluationCloneLabel"
        contentKey="evaluationCloneComfirmMessage"
        open={confirmOpenClone}
        onCancel={confirmOpenActions.setFalse}
        handleConfirm={onCloneConfirm}
      />
    </Container>
  );
};

export default EvaluationList;
